/* global.js — progressive enhancement custom elements. Vanilla, no globals
   except window.themeRoutes / window.themeStrings emitted by layout/theme.liquid. */

const routes = window.themeRoutes || {};
const strings = window.themeStrings || {};

/* ---------------------------------------------------------------------------
 * <header-nav> — mobile menu toggle + tap-to-open submenus on touch devices.
 * ------------------------------------------------------------------------- */
class HeaderNav extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-header-toggle]');
    this.nav = this.querySelector('[data-header-nav]');
    if (this.toggle && this.nav) {
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.toggle.addEventListener('click', this.onToggle);
      this.addEventListener('keydown', this.onKeydown);
    }

    this.querySelectorAll('.header__menu-item--has-children > .header__menu-link').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (window.matchMedia('(max-width: 749px)').matches) {
          event.preventDefault();
          const item = link.closest('.header__menu-item');
          const open = item.getAttribute('data-submenu-open') === 'true';
          item.setAttribute('data-submenu-open', String(!open));
          link.setAttribute('aria-expanded', String(!open));
        }
      });
    });
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    this.removeEventListener('keydown', this.onKeydown);
  }

  onToggle() {
    const open = this.toggle.getAttribute('aria-expanded') === 'true';
    this.setOpen(!open);
  }

  setOpen(open) {
    this.toggle.setAttribute('aria-expanded', String(open));
    this.nav.setAttribute('data-open', String(open));
    if (open) {
      const firstLink = this.nav.querySelector('a');
      if (firstLink) firstLink.focus();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.toggle.getAttribute('aria-expanded') === 'true') {
      this.setOpen(false);
      this.toggle.focus();
    }
  }
}

/* ---------------------------------------------------------------------------
 * <predictive-search> — live search combobox backed by /search/suggest.json.
 * ------------------------------------------------------------------------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-predictive-search-toggle]');
    this.panel = this.querySelector('[data-predictive-search-panel]');
    this.input = this.querySelector('[data-predictive-search-input]');
    this.results = this.querySelector('[data-predictive-search-results]');
    if (!this.toggleBtn || !this.panel || !this.input) return;

    this.debounceTimer = null;
    this.abortController = null;

    this.toggleBtn.addEventListener('click', () => this.setOpen(this.panel.hidden));
    this.input.addEventListener('input', () => this.onInput());
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.setOpen(false);
        this.toggleBtn.focus();
      }
    });
  }

  setOpen(open) {
    this.panel.hidden = !open;
    this.toggleBtn.setAttribute('aria-expanded', String(open));
    if (open) this.input.focus();
  }

  onInput() {
    const term = this.input.value.trim();
    clearTimeout(this.debounceTimer);
    if (term.length < 2) {
      this.results.innerHTML = '';
      return;
    }
    this.debounceTimer = setTimeout(() => this.fetchResults(term), 250);
  }

  async fetchResults(term) {
    if (!routes.searchSuggestUrl) return;
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const url = `${routes.searchSuggestUrl}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,vendor`;

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) return;
      const data = await response.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      this.renderResults(products);
    } catch (error) {
      if (error.name !== 'AbortError') this.results.innerHTML = '';
    }
  }

  renderResults(products) {
    if (!products.length) {
      this.results.innerHTML = `<p class="header__search-empty">${strings.searchNoResults || ''}</p>`;
      return;
    }
    this.results.innerHTML = products
      .map(
        (product) => `
          <a class="header__search-result" href="${product.url}">
            <span class="header__search-result-media media">
              ${product.featured_image ? `<img src="${product.featured_image}" alt="" width="48" height="48" loading="lazy">` : ''}
            </span>
            <span>
              <span class="header__search-result-title">${product.title}</span>
              <span class="header__search-result-price text-secondary">${product.price}</span>
            </span>
          </a>`
      )
      .join('');
  }
}

/* ---------------------------------------------------------------------------
 * <product-gallery> — thumbnail-driven media switcher, no re-fetching.
 * ------------------------------------------------------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
    });
    this.addEventListener('variant:change', (event) => {
      const mediaId = event.detail && event.detail.featuredMediaId;
      if (mediaId) this.showMedia(String(mediaId));
    });
  }

  showMedia(mediaId) {
    if (!mediaId) return;
    let matched = false;
    this.slides.forEach((slide) => {
      const isMatch = slide.dataset.mediaId === mediaId;
      slide.hidden = !isMatch;
      if (isMatch) matched = true;
    });
    if (!matched) return;
    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
    });
  }
}

/* ---------------------------------------------------------------------------
 * <variant-picker> — matches selected options to a variant, updates price /
 * hidden id input / add-to-cart state, and tells <product-gallery> to switch.
 * ------------------------------------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-product-json]');
    if (!dataEl) return;
    try {
      this.variants = JSON.parse(dataEl.textContent).variants;
    } catch (error) {
      this.variants = [];
    }

    this.priceEl = this.querySelector('[data-product-price]');
    this.inventoryEl = this.querySelector('[data-product-inventory]');
    this.idInput = this.querySelector('[data-variant-id-input]');
    this.submitBtn = this.querySelector('[data-add-to-cart]');
    this.submitText = this.querySelector('[data-add-to-cart-text]');

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[name^="option-"]')) this.onOptionChange(event.target);
    });
  }

  onOptionChange(input) {
    const fieldset = input.closest('.main-product__option');
    if (fieldset) {
      const label = fieldset.querySelector('[data-option-selected]');
      if (label) label.textContent = input.value;
      fieldset.querySelectorAll('.main-product__option-value').forEach((el) => {
        const radio = el.querySelector('input');
        el.classList.toggle('is-selected', Boolean(radio && radio.checked));
      });
    }

    const selectedOptions = Array.from(this.querySelectorAll('input[name^="option-"]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((el) => el.value);

    const variant = this.variants.find(
      (v) => JSON.stringify(v.options) === JSON.stringify(selectedOptions)
    );
    if (variant) this.applyVariant(variant);
  }

  applyVariant(variant) {
    if (this.idInput) this.idInput.value = variant.id;

    if (this.priceEl) {
      this.priceEl.innerHTML = variant.compare_at_price
        ? `<span class="price price--sale">${variant.price}</span><s class="price__compare">${variant.compare_at_price}</s>`
        : `<span class="price">${variant.price}</span>`;
    }

    const stickyPrice = this.closest('.main-product') && this.closest('.main-product').querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = variant.price;

    if (this.submitBtn) {
      this.submitBtn.disabled = !variant.available;
      if (this.submitText) {
        this.submitText.textContent = variant.available
          ? this.submitText.dataset.available
          : this.submitText.dataset.soldOut;
      }
    }

    const stickyBtn = document.querySelector('[data-sticky-atc-submit]');
    if (stickyBtn) stickyBtn.disabled = !variant.available;

    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant, featuredMediaId: variant.featured_media_id },
      })
    );
  }
}

/* ---------------------------------------------------------------------------
 * <sticky-atc> — mobile/desktop sticky add-to-cart bar, shown once the
 * in-page buy button scrolls out of view.
 * ------------------------------------------------------------------------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.main-product');
    this.target = section && section.querySelector('[data-add-to-cart]');
    this.submitBtn = this.querySelector('[data-sticky-atc-submit]');

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', () => {
        const realForm = section && section.querySelector('[data-cart-add-form]');
        if (realForm && typeof realForm.requestSubmit === 'function') realForm.requestSubmit();
      });
    }

    if (!this.target || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.setAttribute('data-visible', String(!entry.isIntersecting));
        });
      },
      { rootMargin: '-10% 0px 0px 0px' }
    );
    observer.observe(this.target);
  }
}

/* ---------------------------------------------------------------------------
 * <cart-drawer> — AJAX cart: add / change quantity / remove, re-rendering
 * line items via the Section Rendering API so markup only lives in
 * snippets/cart-drawer-items.liquid.
 * ------------------------------------------------------------------------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('#CartDrawer-Content');
    this.status = this.querySelector('[data-cart-status]');
    this.busy = false;

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-toggle]')) {
        event.preventDefault();
        this.open();
      }
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') this.close();
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.hasAttribute('data-cart-add-form')) return;
      event.preventDefault();
      this.addItem(form);
    });

    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-cart-quantity-change]');
      if (btn) this.changeQuantity(btn.dataset.line, btn.dataset.cartQuantityChange);
    });

    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity-input]');
      if (input) this.changeQuantity(input.dataset.line, input.value);
    });
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
  }

  async addItem(form) {
    if (this.busy || !routes.cartAddUrl) return;
    this.busy = true;
    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer');

    try {
      const response = await fetch(`${routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error((data && data.description) || strings.cartError);
      }
      await this.refresh();
      this.open();
      if (this.status) this.status.textContent = strings.addedToCart || '';
    } catch (error) {
      if (this.status) this.status.textContent = (error && error.message) || strings.cartError || '';
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      this.busy = false;
    }
  }

  async changeQuantity(line, quantity) {
    if (this.busy || !routes.cartChangeUrl) return;
    this.busy = true;
    try {
      await fetch(`${routes.cartChangeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      await this.refresh();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      this.busy = false;
    }
  }

  async refresh() {
    if (!routes.cartDrawerSectionUrl) return;
    const response = await fetch(routes.cartDrawerSectionUrl);
    if (!response.ok) return;
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const freshContent = doc.getElementById('CartDrawer-Content');
    if (freshContent && this.content) this.content.innerHTML = freshContent.innerHTML;

    const freshCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = freshCart.item_count;
    });
  }
}

/* ---------------------------------------------------------------------------
 * <product-recommendations> — lifts server-rendered cards from the Section
 * Rendering API response into the placeholder grid, after first paint.
 * ------------------------------------------------------------------------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    const grid = this.querySelector('[data-recommendations-grid]');
    if (!url || !grid) return;

    fetch(url)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshGrid = doc.querySelector('[data-recommendations-grid]');
        if (freshGrid && freshGrid.innerHTML.trim()) grid.innerHTML = freshGrid.innerHTML;
      })
      .catch(() => {});
  }
}

/* ---------------------------------------------------------------------------
 * Register elements
 * ------------------------------------------------------------------------- */
[
  ['header-nav', HeaderNav],
  ['predictive-search', PredictiveSearch],
  ['product-gallery', ProductGallery],
  ['variant-picker', VariantPicker],
  ['sticky-atc', StickyAtc],
  ['cart-drawer', CartDrawer],
  ['product-recommendations', ProductRecommendations],
].forEach(([name, ctor]) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
});

/* --- Reveal on scroll ----------------------------------------------------- */
(function initReveal() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const elements = document.querySelectorAll('.reveal');
  if (!elements.length) return;

  if (reduceMotion || !('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  elements.forEach((el) => observer.observe(el));
})();
